// Multi-school account store (client-side).
// Keeps each logged-in school account's auth bundle keyed by userId so the user
// can switch schools without re-entering the password. One account is "active"
// at a time (its bundle is copied into the live auth keys the app already reads).

const ACCOUNTS_KEY = 'edu_accounts';        // { [userId]: { userId, schoolName, role, bundle, savedAt } }
const ACTIVE_KEY = 'edu_active_account';    // active userId
const SCHOOLS_KEY = 'edu_school_list';      // discovered schools for the identity [{ userId, schoolName, role }]

// The live localStorage keys the app uses for the active session.
const AUTH_KEYS = [
  'ERPLoginData',
  'access_token',
  'refresh_token',
  'refreshValue',
  'loggedIn',
  'selectedAcademicYear',
  'userAccessData',
];

const readAccounts = () => {
  try { return JSON.parse(localStorage.getItem(ACCOUNTS_KEY)) || {}; } catch (e) { return {}; }
};
const writeAccounts = (obj) => localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(obj));

const snapshotActiveBundle = () => {
  const bundle = {};
  AUTH_KEYS.forEach((k) => { bundle[k] = localStorage.getItem(k); });
  return bundle;
};

const AccountStore = {
  // Snapshot the current live auth keys as this account's bundle and mark it active.
  saveActiveAccount(userData) {
    if (!userData?._id) return;
    const accounts = readAccounts();
    accounts[userData._id] = {
      userId: userData._id,
      schoolName: userData?.Name || '',
      role: userData?.UserRole || '',
      bundle: snapshotActiveBundle(),
      savedAt: Date.now(),
    };
    writeAccounts(accounts);
    localStorage.setItem(ACTIVE_KEY, userData._id);
  },

  getAccounts() {
    return Object.values(readAccounts());
  },

  getActiveUserId() {
    return localStorage.getItem(ACTIVE_KEY);
  },

  hasAccount(userId) {
    return !!readAccounts()[userId];
  },

  // Copy a stored account's bundle into the live auth keys and mark active.
  activate(userId) {
    const acc = readAccounts()[userId];
    if (!acc) return false;
    AUTH_KEYS.forEach((k) => {
      const v = acc.bundle ? acc.bundle[k] : null;
      if (v === null || v === undefined) localStorage.removeItem(k);
      else localStorage.setItem(k, v);
    });
    localStorage.setItem(ACTIVE_KEY, userId);
    return true;
  },

  setDiscoveredSchools(list) {
    localStorage.setItem(SCHOOLS_KEY, JSON.stringify(list || []));
  },

  getDiscoveredSchools() {
    try { return JSON.parse(localStorage.getItem(SCHOOLS_KEY)) || []; } catch (e) { return []; }
  },

  removeAccount(userId) {
    const accounts = readAccounts();
    delete accounts[userId];
    writeAccounts(accounts);
  },

  clearAll() {
    localStorage.removeItem(ACCOUNTS_KEY);
    localStorage.removeItem(ACTIVE_KEY);
    localStorage.removeItem(SCHOOLS_KEY);
  },
};

export default AccountStore;
